import { visibilityPredicates } from './visibility';
import { isFieldNode, isGroupNode } from '@rules/grammar';
import { buildDetailsAndWeightsTree } from '@rules/tree';
import type { RuleSchema, SchemaFieldNode, SchemaGroupNode, Visibility } from '@rules/grammar';

// Boot-time check: every `named:` id in a `visible:` clause must resolve against
// `visibilityPredicates`. FE only checks the visibility surface it evaluates; BE
// checks the full schema (RuleInterpreter.ValidateSchemaReferences).
function assertVisibilityRefs<S>(tree: RuleSchema<S>): void {
  const errors: string[] = [];

  function walkVisibility(v: Visibility, path: string): void {
    switch (v.kind) {
      case 'named':
        if (!(v.named in visibilityPredicates)) {
          errors.push(`${path}: unknown visibility predicate '${v.named}'`);
        }
        return;
      case 'not':
        walkVisibility(v.not, `${path}.not`);
        return;
      case 'all':
        v.all.forEach((sub, i) => walkVisibility(sub, `${path}.all[${i}]`));
        return;
      case 'any':
        v.any.forEach((sub, i) => walkVisibility(sub, `${path}.any[${i}]`));
        return;
    }
  }

  function walkNode(node: SchemaFieldNode<S> | SchemaGroupNode<S>, path: string): void {
    walkVisibility(node.visible, `${path}.visible`);
    if (isGroupNode(node)) {
      for (const [k, child] of Object.entries(node.children)) {
        walkNode(child, `${path}.children.${k}`);
      }
    } else if (isFieldNode(node)) {
      // No rule walk — rules are BE-only now.
    }
  }

  for (const [k, root] of Object.entries(tree)) walkNode(root, k);

  if (errors.length > 0) {
    throw new Error(
      'Rule tree references unknown visibility predicates:\n  - ' + errors.join('\n  - ')
    );
  }
}

function loadAndValidate() {
  const raw = buildDetailsAndWeightsTree();
  assertVisibilityRefs(raw);
  return Object.freeze(raw);
}

export const detailsAndWeightsTree = loadAndValidate();

type Join<P extends string, K extends string> = P extends '' ? K : `${P}.${K}`;

export type TreePaths<T, Prefix extends string = ''> = {
  readonly [K in keyof T & string]: T[K] extends { children: infer Ch }
    ? TreePaths<Ch, Join<Prefix, K>> & { readonly $path: Join<Prefix, K> }
    : Join<Prefix, K>;
};

function isGroup(node: unknown): node is { children: Record<string, unknown> } {
  return typeof node === 'object' && node !== null && 'children' in node;
}

function buildPaths(node: unknown, path: string): unknown {
  if (!isGroup(node)) return path;
  const result: Record<string, unknown> = { $path: path };
  for (const [k, child] of Object.entries(node.children)) {
    result[k] = buildPaths(child, path ? `${path}.${k}` : k);
  }
  return result;
}

export function treePaths<T>(tree: T): TreePaths<T> {
  const result: Record<string, unknown> = {};
  for (const [k, node] of Object.entries(tree as Record<string, unknown>)) {
    result[k] = buildPaths(node, k);
  }
  return result as TreePaths<T>;
}

export const paths = treePaths(detailsAndWeightsTree);
